import { useEffect } from "react";
import {
  Box,
  Dialog,
  Typography,
  IconButton,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

export interface VideoPlayerState {
  url: string;
  title: string;
  skippedCount: number;
}

interface VideoPlayerDialogProps {
  state: VideoPlayerState | null;
  onClose: () => void;
  /** Override z-index (e.g. 1400 when opening atop another modal) */
  zIndex?: number;
}

/**
 * "3 plays have no video". Skips now include ctg chances with no SecondSpectrum
 * twin — plays the two providers cut differently — not only missing footage
 * (F1 contract §5).
 */
export function skippedVideoMessage(skippedCount: number): string {
  return `${skippedCount} play${skippedCount === 1 ? " has" : "s have"} no video`;
}

export default function VideoPlayerDialog({
  state,
  onClose,
  zIndex,
}: VideoPlayerDialogProps) {
  // Close on ESC — the iframe captures focus so Dialog's built-in handler
  // doesn't fire. Listen on window instead.
  useEffect(() => {
    if (!state) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [state, onClose]);
  return (
    <Dialog
      open={Boolean(state)}
      onClose={onClose}
      fullScreen
      PaperProps={{ sx: { bgcolor: "#1a1a1a" } }}
      sx={zIndex != null ? { zIndex } : undefined}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 1.5,
          py: 0.5,
          bgcolor: "#4c4c4c",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Typography
            sx={{
              color: "#fff",
              fontWeight: 700,
              fontSize: "0.8rem",
              textTransform: "uppercase",
            }}
          >
            {state?.title}
          </Typography>
          {(state?.skippedCount ?? 0) > 0 && (
            <Typography
              sx={{
                color: "rgba(255,255,255,0.6)",
                fontSize: "0.7rem",
                whiteSpace: "nowrap",
              }}
            >
              ({skippedVideoMessage(state!.skippedCount)})
            </Typography>
          )}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography
            component="span"
            onClick={() => state && window.open(state.url, "_blank")}
            sx={{
              color: "#fff",
              fontSize: "0.75rem",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              opacity: 0.8,
              "&:hover": { opacity: 1 },
              transition: "opacity 0.15s",
            }}
          >
            Open in new tab
            <OpenInNewIcon sx={{ fontSize: "0.875rem" }} />
          </Typography>
          <IconButton size="small" onClick={onClose} sx={{ color: "#fff" }}>
            <CloseIcon sx={{ fontSize: "1rem" }} />
          </IconButton>
        </Box>
      </Box>
      {state && (
        <Box sx={{ flex: 1, position: "relative" }}>
          <CircularProgress
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              color: "#fff",
            }}
          />
          <iframe
            src={state.url}
            title="CTG Video Player"
            style={{
              width: "100%",
              height: "100%",
              border: "none",
              position: "relative",
              zIndex: 1,
            }}
          />
        </Box>
      )}
    </Dialog>
  );
}
